import {SURVEY_LAYERS, type SurveyLayer,} from "../../constants/SurveyLayers";
import ViewModeConstants from "../../constants/ViewModeConstants";
import {Survey} from "../../components/Prescription/PrescriptionJob";
import {SurveyLayerViewCapabilities, SurveyViewCapabilities} from "../../helpers/SurveyUtils";
import {
  getActiveSurveyCountryCode,
  getClassificationImageAliasKeys,
  isCustomClassificationLayer,
} from './country/SurveyCountryStrategy';

export const LEGACY_IMAGES_FOLDER = "images";
export const CURRENT_IMAGES_FOLDER = "newImages";

export const NORMALIZED_IMAGES_KEY = "NORMALIZED";
export const RAW_IMAGES_KEY = "RAW";
export const COARSE_CLASSIFICATION_IMAGES_KEY = "COARSE_CLASSIFICATION";
export const FINE_CLASSIFICATION_IMAGES_KEY = "FINE_CLASSIFICATION";
export const FINE_MAPPINGS_KEY = "FINE";

export const getImageUrl = (images: Object, layer: SurveyLayer, classificationsEnabled: Boolean, variationsEnabled: Boolean, viewMode: string) => {

  if (!images) {
    console.error("No images not found for layer: ", layer);
    return null;
  }

  const folder = images[layer];

  if (!folder) {
    console.error("Images not found for layer: ", layer, ". Images available for following layers: ", Object.keys(images));
  }

  let image;
  if (folder) {
    if (variationsEnabled || layer === SURVEY_LAYERS.ALTITUDE) {
      image = folder[NORMALIZED_IMAGES_KEY];
    } else if (classificationsEnabled) {
      image = getClassificationURL(folder, layer, viewMode);
    } else {
      image = folder[RAW_IMAGES_KEY];
    }
  }

  return image;
};

const getClassificationURL = (images: Object, layer: SurveyLayer, viewMode: string) => {
  const aliases = getClassificationImageAliasKeys(
    layer,
    getActiveSurveyCountryCode(),
    viewMode
  );

  for (let i = 0; i < aliases.length; i++) {
    const aliasKey = aliases[i];
    if (images[aliasKey] != null) {
      return images[aliasKey];
    }
  }

  if (viewMode === ViewModeConstants.OVERVIEW) {
    return images[COARSE_CLASSIFICATION_IMAGES_KEY];
  }
  return images[FINE_CLASSIFICATION_IMAGES_KEY];
};

/**
 * Returns true if the given survey contains both coarse and fine demand alias images
 * for the specified layer and country code. Used to decide whether to fetch
 * classification values for a specific target survey, bypassing stale React context.
 */
export const surveyHasDemandImages = (survey: Survey, layer: SurveyLayer, countryCode: ?string): boolean => {
  if (!survey || !layer) {
    return false;
  }
  const images = survey[CURRENT_IMAGES_FOLDER];
  const imagesLegacy = survey[LEGACY_IMAGES_FOLDER];
  const folder = (images && images[layer]) || (imagesLegacy && imagesLegacy[layer]);
  if (!folder) {
    return false;
  }
  const coarseAliases = getClassificationImageAliasKeys(layer, countryCode, ViewModeConstants.OVERVIEW);
  const fineAliases = getClassificationImageAliasKeys(layer, countryCode, ViewModeConstants.ANALYSIS);
  const hasCoarseAlias = coarseAliases.some((key) => folder[key] != null);
  const hasFineAlias = fineAliases.some((key) => folder[key] != null);
  const isCustomClassification = isCustomClassificationLayer(layer, countryCode);
  const hasFineClassification = folder[FINE_CLASSIFICATION_IMAGES_KEY] != null;
  return (hasCoarseAlias && hasFineAlias) || (isCustomClassification && (hasFineAlias || hasFineClassification));
};

export const extractSurveyCapabilities = (surveys: Array<Survey>): SurveyViewCapabilities => {
  const result = new SurveyViewCapabilities();
  Object.values(SURVEY_LAYERS).forEach((layer) => {
    const overviewLayerCapabilities = new SurveyLayerViewCapabilities();
    surveys.filter(Boolean).forEach((survey) => {
      const surveyCapabilities = result.singleField.get(survey.surveyImageId) || new Map();
      const singleFieldLayerCapabilities = surveyCapabilities.get(layer) || new SurveyLayerViewCapabilities();

      const images = survey[CURRENT_IMAGES_FOLDER];
      const imagesLegacy = survey[LEGACY_IMAGES_FOLDER];
      const folder = (images && images[layer]) || (imagesLegacy && imagesLegacy[layer]);

      if (folder) {
        const countryCode = getActiveSurveyCountryCode();
        const coarseAliases = getClassificationImageAliasKeys(layer, countryCode, ViewModeConstants.OVERVIEW);
        const fineAliases = getClassificationImageAliasKeys(layer, countryCode, ViewModeConstants.ANALYSIS);
        const hasCoarseAlias = coarseAliases.some((key) => folder[key] != null);
        const hasFineAlias = fineAliases.some((key) => folder[key] != null);
        const isCustomClassification = isCustomClassificationLayer(layer, countryCode);

        const overviewHasClassification = folder["COARSE_CLASSIFICATION"] != null || hasCoarseAlias;
        const singleFieldHasClassification = folder["FINE_CLASSIFICATION"] != null || hasFineAlias;

        overviewLayerCapabilities.enableRaw = overviewLayerCapabilities.enableRaw || folder["RAW"] != null;
        overviewLayerCapabilities.enableVariations = false;
        overviewLayerCapabilities.enableClassifications = overviewLayerCapabilities.enableClassifications || overviewHasClassification;
        overviewLayerCapabilities.enableCustomClassification = overviewLayerCapabilities.enableCustomClassification || (hasCoarseAlias && hasFineAlias);

        singleFieldLayerCapabilities.enableRaw = folder["RAW"] != null;
        singleFieldLayerCapabilities.enableVariations = folder["NORMALIZED"] != null;
        singleFieldLayerCapabilities.enableClassifications = singleFieldHasClassification;
        singleFieldLayerCapabilities.enableCustomClassification = (hasCoarseAlias && hasFineAlias) || (isCustomClassification && singleFieldHasClassification);

        surveyCapabilities.set(layer, singleFieldLayerCapabilities);
        result.singleField.set(survey.surveyImageId, surveyCapabilities);
      }
    });

    result.overview.set(layer, overviewLayerCapabilities);
  });
  return result;
};
